/**
 * Matriz de permisos por rol, espejo de `core.role_permissions`.
 *
 * Es lo que consulta la UI (`usePermissions`, `RequirePermission`): el JWT trae
 * los ROLES y esta tabla dice qué habilita cada uno. El backend valida con
 * `core.authorize()` contra la base.
 *
 * 🔴 Si cambiás esta matriz, cambiá también la base (una migración) y la lista
 *    esperada de `tests/rls/460_matriz_de_permisos.sql`. Hay dos guardas y las
 *    dos rompen si no: `tests/unit/matriz-de-permisos.test.ts` compara este
 *    archivo con esa lista (gate de commit) y el test RLS 460 compara la lista
 *    con la base (gate de push). SEG-01: sin ellas el archivo se desfasó en
 *    silencio y le escondía a Hugo /backlogs y las passkeys.
 *
 * superadmin no aparece — tiene bypass implícito (todo permitido).
 */

import type { AppPermission } from '@/types/app-permissions';
import type { AppRole } from '@/types/app-roles';

export const ROLE_PERMISSIONS: Record<
  Exclude<AppRole, 'superadmin'>,
  AppPermission[]
> = {
  admin_dendev: [
    'prospects.read.all',
    'prospects.write.all',
    'prospects.delete.all',
    'prospects.share.all',
    'prospects.directory.view',
    'case_studies.view',
    'case_studies.manage',
    'prospects.export.all',
    'leads.read.all',
    'leads.read.shared',
    'leads.write.all',
    'campaigns.read.all',
    'campaigns.write.all',
    'campaigns.send.all',
    'catalog.read.all',
    'catalog.write.all',
    'ai_rules.read.all',
    'organization.read.all',
    'organization.write.all',
    'vocabularies.read.all',
    'vocabularies.write.own',
    'workflows.read.all',
    'denylist.read.all',
    'agent_logs.read.all',
    'audit.read',
    'employees.view',
    'employees.manage',
    'areas.view',
    'areas.manage',
    'positions.view',
    'positions.manage',
    'tasks.view',
    'tasks.create',
    'tasks.assign_others',
    'tasks.manage_all',
    'tasks.view_area',
    'newsletters.manage',
    'user_ai_context.read.own',
    'user_ai_context.write.own',
    // Chat (Fase B-2)
    'channels.view_public',
    'channels.create_public',
    'channels.create_private',
    'channels.manage_all',
    'messages.send',
    'attachments.upload',
    'projects.view',
    'projects.create',
    'projects.manage_all',
    // Cobranzas & Comisiones — seed completo (migración 20260814100001).
    // view.own además de view.all: el ítem de nav gatea por view.own.
    'collections.view.own',
    'collections.view.all',
    'collections.record.own',
    'collections.manage',
    // Backlogs unificados (seed de backlog-unificado, 2026-08-29).
    'backlogs.view',
    'backlogs.manage',
    // Métricas de soporte: desempeño por persona, solo gerencia
    // (seed de soporte-metricas-de-respuesta, 2026-10-02).
    'support.metrics.view',
    // Estaban en la base y no acá (SEG-01).
    'campaigns.read.own',
    'campaigns.write.own',
    'leads.read.own',
    'leads.write.own',
    'passkeys.view',
    'passkeys.manage',
    'passkeys.manage_any',
    'telegram.users.manage',
    // Gastos fijos (change gastos-fijos, 2026-11-02).
    'expenses.manage',
  ],
  // Directivo · CSO, CTO y los que se sumen (el CEO es superadmin). Todo lo de
  // employee (el rol se sostiene solo) más la parte COMERCIAL de todos:
  // prospectos (fusionar incluido), casos de éxito, campañas, leads y asignar
  // tareas. Nada administrativo: sin IA de la organización, facturación,
  // cobranzas ajenas, catálogo, equipo, usuarios ni proyectos de todos.
  // Espejo de la migración 20261028120100, que verifica ese perímetro.
  directivo: [
    'prospects.read.own',
    'prospects.read.shared',
    'prospects.write.own',
    'prospects.write.shared',
    'prospects.delete.own',
    'prospects.share.own',
    'prospects.directory.view',
    'prospects.export.own',
    'leads.read.own',
    'leads.read.shared',
    'leads.write.own',
    'campaigns.read.own',
    'campaigns.read.assigned',
    'campaigns.write.own',
    'campaigns.write.assigned',
    'campaigns.send.own',
    'catalog.read.all',
    'organization.read.all',
    'vocabularies.write.own',
    'tasks.view',
    'tasks.create',
    'tasks.view_area',
    'newsletters.manage',
    'user_ai_context.read.own',
    'user_ai_context.write.own',
    'channels.view_public',
    'channels.create_private',
    'messages.send',
    'attachments.upload',
    'projects.view',
    'projects.create',
    'collections.view.own',
    'collections.record.own',
    'backlogs.view',
    'passkeys.view',
    'passkeys.manage',
    // Lo comercial de todos
    'prospects.read.all',
    'prospects.write.all',
    'prospects.share.all',
    'prospects.export.all',
    'case_studies.view',
    'campaigns.read.all',
    'campaigns.write.all',
    'leads.read.all',
    'leads.write.all',
    'tasks.assign_others',
    'areas.view',
    'positions.view',
  ],
  // ⚠️ NO incluir aquí: ai_rules.read.all, employees.view, areas.view,
  // positions.view. El employee NO entra al Hub IA ni al directorio de
  // /equipo por default. Quien lo necesite (ej. Hugo de RRHH, un dev)
  // lo recibe vía core.user_permission_overrides — el JWT trae el array
  // en user_extra_permissions y usePermissions lo suma a los de rol.
  // Ver .claude/SEGURIDAD_PERMISOS_RBAC_v1.md §4.
  employee: [
    'prospects.read.own',
    'prospects.read.shared',
    'prospects.write.own',
    'prospects.write.shared',
    'prospects.delete.own',
    'prospects.share.own',
    'prospects.directory.view',
    'prospects.export.own',
    'leads.read.own',
    'leads.read.shared',
    'leads.write.own',
    'campaigns.read.own',
    'campaigns.read.assigned',
    'campaigns.write.own',
    'campaigns.write.assigned',
    'campaigns.send.own',
    'catalog.read.all',
    'organization.read.all',
    'vocabularies.write.own',
    'tasks.view',
    'tasks.create',
    // Agenda del área: ve las tareas de equipo de las áreas donde tiene
    // assignment vigente. El vendedor NO lo tiene (ve por asignación).
    'tasks.view_area',
    'newsletters.manage',
    'user_ai_context.read.own',
    'user_ai_context.write.own',
    // Chat (Fase B-2) — sin manage_all, sin create_public
    'channels.view_public',
    'channels.create_private',
    'messages.send',
    'attachments.upload',
    'projects.view',
    'projects.create',
    // Cobranzas de la cartera propia (migración 20260818120003). Cada empleado
    // cobra y hace seguimiento de sus clientes; `.own` no abre nada ajeno
    // (las policies combinan permiso + contracts.salesperson_id).
    'collections.view.own',
    'collections.record.own',
    'backlogs.view',
    'passkeys.view',
    'passkeys.manage',
  ],
  // Vendedor · perímetro MENOR que employee. El RBAC es solo aditivo, así que
  // esto NO se puede expresar con overrides sobre employee: por eso es un rol.
  // Deltas contra employee: sin channels.view_public (solo su canal de área,
  // los canales donde lo sumaron y sus DMs), sin channels.create_private, sin
  // projects.*, sin tasks.view_area (agenda por asignación), sin
  // newsletters.manage, sin prospects.export.own (que no se lleve la cartera).
  // Espejo de la migración 20260818120001.
  salesperson: [
    'prospects.read.own',
    'prospects.read.shared',
    'prospects.write.own',
    'prospects.write.shared',
    'prospects.delete.own',
    'prospects.share.own',
    'prospects.directory.view',
    'case_studies.view',
    'leads.read.own',
    'leads.write.own',
    'campaigns.read.own',
    'campaigns.read.assigned',
    'campaigns.write.own',
    'campaigns.write.assigned',
    'campaigns.send.own',
    'catalog.read.all',
    'organization.read.all',
    'vocabularies.write.own',
    'tasks.view',
    'tasks.create',
    'user_ai_context.read.own',
    'user_ai_context.write.own',
    'messages.send',
    'attachments.upload',
    'collections.view.own',
    'collections.record.own',
    'passkeys.view',
    'passkeys.manage',
  ],
  // Sin permisos en la base: el seed de 20260519000006 nunca quedó aplicado y
  // nadie tiene el rol. Acá tampoco, así la UI no muestra lo que la RLS niega.
  external_legacy: [],
  guest: ['prospects.read.shared'],
};

/**
 * Implicación de scopes: `x.action.all` cubre a cualquier scope menor.
 *
 * Convención del proyecto: los permisos siguen `{resource}.{action}.{scope}`
 * (ej. `prospects.read.own`, `campaigns.write.assigned`). El scope `all` es
 * universal por definición — si tenés `prospects.read.all` podés leer
 * cualquier prospect, incluyendo los `own`, `shared` y los que te están
 * `assigned`. Sin esta implicación, `admin_dendev` (que tiene `.all`) se
 * comía 403 en cada `GuardedPage permission="x.own"`.
 *
 * Mantenemos la regla acotada a `.all` para no inventar implicaciones
 * laterales (`shared` NO implica `own` ni viceversa — son dimensiones
 * independientes).
 */
export function permissionMatches(
  granted: AppPermission,
  requested: AppPermission,
): boolean {
  if (granted === requested) return true;
  const gParts = granted.split('.');
  const rParts = requested.split('.');
  if (gParts.length !== 3 || rParts.length !== 3) return false;
  // Mismo resource + mismo action, y el granted está en scope `all`.
  return (
    gParts[0] === rParts[0] && gParts[1] === rParts[1] && gParts[2] === 'all'
  );
}
